import { useEffect } from "react";
import { Check, Plus, RotateCw, Trash2 } from "lucide-react";
import type { Furniture, PlacedModule, SectionalModuleId } from "../../lib/types";
import { SECTIONAL_MODULES } from "../../lib/furniture/modules";
import { addModule, applyModules, freePorts, removeModule, rotateModule } from "../../lib/furniture/modularEdit";
import { FurniturePreview } from "../FurniturePreview";
import { cn } from "../../lib/cn";
import { track } from "../../lib/telemetry";
import { isRender3dOpen } from "../../lib/render3dRequests";

interface Props {
  furniture: Furniture;
  modules: PlacedModule[];
  selectedModuleId: string | null;
  onSelectModule: (id: string | null) => void;
  onCommit: (patch: Partial<Furniture>) => void;
  onDone: () => void;
}

const TRAY_ORDER: SectionalModuleId[] = [
  "seat",
  "seat-arm-left",
  "seat-arm-right",
  "corner",
  "corner-curved",
  "chaise-left",
  "chaise-right",
  "ottoman",
];

/**
 * Floating DOM toolbar shown while editing a modular sectional: a strip of
 * module thumbnails to add, plus rotate / remove for the selected module and
 * a Done button. Delete/Backspace removes the selected module; Escape exits.
 */
export function ModuleTray({
  furniture: f,
  modules,
  selectedModuleId,
  onSelectModule,
  onCommit,
  onDone,
}: Props) {
  const selected = modules.find((m) => m.id === selectedModuleId) ?? null;
  const canRemove = !!selected && modules.length > 1;
  const openPorts = freePorts(modules).length;
  const canAdd = openPorts > 0;

  function add(id: SectionalModuleId) {
    if (!canAdd) return;
    // Attach near the selected module when there is one, else anywhere free.
    const near = selected ? { x: selected.x, y: selected.y } : undefined;
    const next = addModule(modules, id, near);
    onCommit(applyModules(f, next));
    onSelectModule(next[next.length - 1].id);
    track("sectional_module_added", { module: id });
  }

  function rotate() {
    if (!selected) return;
    onCommit(applyModules(f, rotateModule(modules, selected.id)));
  }

  function remove() {
    if (!selected || !canRemove) return;
    onCommit(applyModules(f, removeModule(modules, selected.id)));
    onSelectModule(null);
    track("sectional_module_removed", { module: selected.moduleId });
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (isRender3dOpen()) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (e.key === "Escape") {
        e.preventDefault();
        onDone();
      } else if ((e.key === "Delete" || e.key === "Backspace") && selected) {
        e.preventDefault();
        remove();
      } else if (e.key.toLowerCase() === "r" && selected && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        rotate();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, modules, f]);

  return (
    <div
      // Above the desktop area badge, and above the selection bar on phones.
      className="pointer-events-auto absolute inset-x-0 bottom-[max(3.5rem,var(--canvas-overlay-bottom))] z-20 flex justify-center px-3"
      data-testid="module-tray"
    >
      <div className="flex max-w-full flex-col items-stretch gap-1 rounded-2xl bg-white/95 p-2 shadow-xl ring-1 ring-ink-200 backdrop-blur">
        {!canAdd && (
          <div className="px-1 text-caption text-amber-700" data-testid="module-tray-hint">
            Every end is capped by an arm — remove an arm module to keep extending.
          </div>
        )}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
          {TRAY_ORDER.map((id) => {
            const def = SECTIONAL_MODULES[id];
            return (
              <button
                key={id}
                type="button"
                title={canAdd ? `Add ${def.label}` : "No open end to attach to"}
                aria-label={`Add ${def.label}`}
                disabled={!canAdd}
                onClick={() => add(id)}
                className={cn(
                  "group flex shrink-0 flex-col items-center gap-0.5 rounded-xl px-1.5 py-1 transition-all",
                  canAdd ? "hover:bg-ink-50 active:scale-95" : "opacity-40 cursor-not-allowed"
                )}
              >
                <div className="relative">
                  <FurniturePreview
                    furniture={{
                      name: def.label,
                      width: def.w,
                      depth: def.d,
                      height: f.height,
                      color: f.color,
                      material: f.material,
                      model: { kind: "modular", modules: [{ id: "p", moduleId: id, x: 0, y: 0, rotation: 0 }] },
                    }}
                    size={40}
                    pad={3}
                    shadow={false}
                  />
                  <span className="absolute -right-1 -top-1 inline-flex size-4 items-center justify-center rounded-full bg-ink-900 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <Plus className="size-3" />
                  </span>
                </div>
                <span className="text-label leading-none text-ink-500 whitespace-nowrap">{def.label}</span>
              </button>
            );
          })}
        </div>
        <div className="mx-1 h-8 w-px bg-ink-200" />
        <button
          type="button"
          onClick={rotate}
          disabled={!selected}
          title="Rotate module (R)"
          aria-label="Rotate module"
          className={cn(
            "inline-flex size-9 items-center justify-center rounded-xl ring-1 transition-colors",
            selected ? "bg-white text-ink-700 ring-ink-200 hover:bg-ink-50" : "bg-ink-50 text-ink-300 ring-ink-100"
          )}
        >
          <RotateCw className="size-4" />
        </button>
        <button
          type="button"
          onClick={remove}
          disabled={!canRemove}
          title="Remove module (Delete)"
          aria-label="Remove module"
          className={cn(
            "inline-flex size-9 items-center justify-center rounded-xl ring-1 transition-colors",
            canRemove ? "bg-white text-rose-600 ring-ink-200 hover:bg-rose-50" : "bg-ink-50 text-ink-300 ring-ink-100"
          )}
        >
          <Trash2 className="size-4" />
        </button>
        <button
          type="button"
          onClick={onDone}
          data-testid="module-tray-done"
          className="ml-1 inline-flex h-9 items-center gap-1.5 rounded-xl bg-ink-900 px-3 text-xs font-semibold text-white hover:bg-ink-800 transition-colors"
        >
          <Check className="size-4" />
          Done
        </button>
      </div>
      </div>
    </div>
  );
}
